"use client";

import { Heart, Loader2, type LucideIcon } from "lucide-react";
import { useMemo, useTransition } from "react";

import {
  selectIsVariantInWishlist,
  wishlistApi,
} from "../wishlistApi";
import { useWishlist } from "../hooks/useWishlist";
import { useAppSelector } from "@/src/store/hooks";

type WishlistToggleButtonProps = {
  variantId: number | null;
  title: string;
  className?: string;
  filledClassName?: string;
  label?: string;
  hideIcon?: boolean;
  icon?: LucideIcon;
};

export default function WishlistToggleButton({
  variantId,
  title,
  className = "inline-flex h-[2.8rem] w-[2.8rem] items-center justify-center rounded-full border border-[rgba(238,49,106,0.2)] bg-[var(--color-surface)] text-[var(--color-ink)] shadow-[0_12px_25px_rgba(238,49,106,0.08)] max-[420px]:h-[2.55rem] max-[420px]:w-[2.55rem] hover:-translate-y-[2px] hover:border-[rgba(238,49,106,0.4)] hover:bg-[var(--color-surface-strong)]",
  filledClassName = "bg-[rgba(255,169,0,0.14)] text-[var(--color-accent-deep)]",
  label,
  hideIcon = false,
  icon: Icon = Heart,
}: WishlistToggleButtonProps) {
  const [isPending, startLocalTransition] = useTransition();
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  const isAuthInitialized = useAppSelector((state) => state.auth.isInitialized);
  const isInWishlistSelector = useMemo(
    () =>
      typeof variantId === "number"
        ? selectIsVariantInWishlist(variantId)
        : null,
    [variantId],
  );
  const isInWishlist = useAppSelector((state) =>
    isInWishlistSelector ? isInWishlistSelector(state) : false,
  );
  wishlistApi.useGetWishlistQuery(undefined, {
    skip: !isAuthenticated || typeof variantId !== "number",
    selectFromResult: () => ({}),
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });
  const {
    isMutating,
    pendingVariantId,
    toggleItem,
  } = useWishlist();
  const isActive = isInWishlist;
  const isThisVariantPending =
    typeof variantId === "number" &&
    pendingVariantId === variantId &&
    (isPending || isMutating);
  const isDisabled = !isAuthInitialized || !variantId || isThisVariantPending;

  return (
    <button
      type="button"
      className={[
        className,
        isDisabled ? "cursor-not-allowed opacity-60" : "",
        isActive ? filledClassName : "",
      ]
        .filter(Boolean)
        .join(" ")}
      aria-label={
        isActive
          ? `أزل ${title} من المفضلة`
          : `أضف ${title} إلى المفضلة`
      }
      aria-pressed={isActive}
      disabled={isDisabled}
      onClick={() => {
        if (!variantId) {
          return;
        }

        startLocalTransition(() => {
          void toggleItem(variantId);
        });
      }}
    >
      {isThisVariantPending ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : (
        <>
          {!hideIcon ? (
            <Icon className={`h-4 w-4 ${isActive && Icon === Heart ? "fill-current " : ""}`} />
          ) : null}
          {label ? <span>{label}</span> : null}
        </>
      )}
    </button>
  );
}



